<style>
@import "@/assets/css/custom.css";
</style>

<template>
	<div>
		<Breadcrumbs main="Accounts" title="Courier Details" />

		<div class="container-fluid">
			<div v-if="loading" class="card">
				<div class="card-body text-center py-5">
					<span class="spinner-border spinner-border-sm me-2"></span>
					Loading courier details…
				</div>
			</div>

			<div v-else-if="loadError" class="card">
				<div class="card-body text-center py-5">
					<p class="text-danger mb-3">{{ loadError }}</p>
					<button type="button" class="btn btn-light" @click="$router.push({ name: 'courier_account' })">Back to courier list</button>
				</div>
			</div>

			<div v-else-if="courier" class="card">
				<div class="card-body">
					<div class="d-flex justify-content-between align-items-start flex-wrap gap-2 mb-3">
						<div>
							<h5 class="mb-1">Courier #{{ courier.id }}</h5>
							<div class="small text-muted">
								<div><strong>Party:</strong> {{ userTypeLabel(courier.request_user_type) }} — {{ courier.request_user_name || "—" }}</div>
								<div><strong>Contact:</strong> {{ courier.request_user_contact || "—" }}</div>
								<div><strong>Total devices:</strong> {{ courier.total_devices ?? "—" }}</div>
								<div><strong>Invoice:</strong> {{ courier.invoice_number || "—" }}</div>
								<div><strong>Courier status:</strong> {{ courierStatusLabel(courier.courier_status) }}</div>
								<div><strong>Recorded:</strong> {{ courier.created_at ? $filters.formatDate(courier.created_at) : "—" }}</div>
								<div><strong>Added by:</strong> {{ courier.added_user_name || "—" }}</div>
								<div><strong>Updated by:</strong> {{ courier.updated_user_name || "—" }}</div>
							</div>
						</div>
						<button type="button" class="btn btn-light btn-sm" @click="$router.push({ name: 'courier_account' })">Back</button>
					</div>

					<div class="card border-light mb-4">
						<div class="card-body py-3">
							<div class="row text-center small">
								<div class="col-md-2">
									<span class="text-muted d-block">Grand total</span>
									<span class="fw-bold">₹{{ formatMoney(summary?.grand_total) }}</span>
								</div>
								<div class="col-md-2">
									<span class="text-muted d-block">Paid</span>
									<span class="fw-bold text-success">₹{{ formatMoney(summary?.paid_amount) }}</span>
								</div>
								<div class="col-md-2">
									<span class="text-muted d-block">Pending</span>
									<span class="fw-bold">₹{{ formatMoney(summary?.pending_amount) }}</span>
								</div>
								<div class="col-md-2">
									<span class="text-muted d-block">Online paid</span>
									<span class="fw-bold">₹{{ formatMoney(summary?.online_amount) }}</span>
								</div>
								<div class="col-md-2">
									<span class="text-muted d-block">Cash paid</span>
									<span class="fw-bold">₹{{ formatMoney(summary?.cash_in_hand) }}</span>
								</div>
								<div class="col-md-2">
									<span class="text-muted d-block">Payment status</span>
									<span class="badge rounded-pill" :class="paymentStatusClass(summary?.payment_status)">
										{{ summary?.payment_status_label || paymentStatusLabel(summary?.payment_status) }}
									</span>
								</div>
							</div>
						</div>
					</div>

					<div class="mt-2 mb-4">
						<h6 class="border-bottom pb-2">Products</h6>
						<div class="table-responsive">
							<table class="table table-sm table-bordered align-middle">
								<thead class="table-light">
									<tr align="center">
										<th>Product</th>
										<th>Qty</th>
										<th>Amount (₹)</th>
										<th>GST (₹)</th>
										<th>Total (₹)</th>
										<th>Paid (₹)</th>
										<th>Pending (₹)</th>
										<th>Payment mode</th>
										<th>Bank / Payment ID</th>
									</tr>
								</thead>
								<tbody>
									<tr v-for="p in products" :key="'cp-' + p.id" align="center">
										<td class="text-start">{{ productTypeLabel(p.product_type) || p.product_type_label || "—" }}</td>
										<td>{{ p.quantity }}</td>
										<td>₹{{ formatMoney(p.amount) }}</td>
										<td>₹{{ formatMoney(p.gst) }}</td>
										<td>₹{{ formatMoney(p.total) }}</td>
										<td>₹{{ formatMoney(p.payment_transaction?.paid_amount) }}</td>
										<td>₹{{ formatMoney(p.payment?.pending_amount) }}</td>
										<td>{{ paymentModeLabel(p.payment?.payment_mode) }}</td>
										<td class="text-start small">
											<div>{{ p.payment?.bank_account?.bank_name || "—" }}</div>
											<div class="text-muted">{{ p.payment?.payment_id || "—" }}</div>
										</td>
									</tr>
								</tbody>
							</table>
						</div>
					</div>

					<div v-if="primaryPayment" class="mt-2 mb-4">
						<h6 class="border-bottom pb-2">Current payment</h6>
						<div class="row small">
							<div class="col-md-3"><strong>Payment date:</strong> {{ primaryPayment.payment_date ? $filters.formatDate(primaryPayment.payment_date) : "—" }}</div>
							<div class="col-md-3"><strong>Online:</strong> ₹{{ formatMoney(primaryPayment.online_amount) }}</div>
							<div class="col-md-3"><strong>Cash:</strong> ₹{{ formatMoney(primaryPayment.cash_amount) }}</div>
							<div class="col-md-3"><strong>Credit:</strong> ₹{{ formatMoney(primaryPayment.credit_amount) }}</div>
							<div class="col-md-3 mt-2"><strong>Opening balance:</strong> ₹{{ formatMoney(primaryPayment.opening_balance) }}</div>
							<div class="col-md-3 mt-2"><strong>Closing balance:</strong> ₹{{ formatMoney(primaryPayment.closing_balance) }}</div>
							<div class="col-md-3 mt-2"><strong>Added by:</strong> {{ primaryPayment.added_by_name || "—" }}</div>
							<div class="col-md-12 mt-2"><strong>Remarks:</strong> {{ primaryPayment.remarks || "—" }}</div>
						</div>
					</div>

					<div class="mt-2 mb-2">
						<h6 class="border-bottom pb-2">Payment logs</h6>
						<p v-if="!paymentLogs.length" class="text-muted mb-0">No payment logs for this courier.</p>
						<div v-else class="table-responsive">
							<table class="table table-sm table-bordered align-middle">
								<thead class="table-light">
									<tr align="center">
										<th>#</th>
										<th>Transaction amount (₹)</th>
										<th>Status</th>
										<th>Pending (₹)</th>
										<th>Credit (₹)</th>
										<th>Total (₹)</th>
										<th>Bank</th>
										<th>Payment ID</th>
										<th>Payment date</th>
										<th>Recorded on</th>
									</tr>
								</thead>
								<tbody>
									<tr v-for="(log, idx) in paymentLogs" :key="'cpl-' + log.id" align="center">
										<td>{{ idx + 1 }}</td>
										<td>₹{{ formatMoney(log.transaction_amount) }}</td>
										<td>
											<span class="badge rounded-pill" :class="paymentStatusClass(log.status)">
												{{ log.status_label || paymentStatusLabel(log.status) }}
											</span>
										</td>
										<td>₹{{ formatMoney(log.pending_amount) }}</td>
										<td>₹{{ formatMoney(log.credit_amount) }}</td>
										<td>₹{{ formatMoney(log.total_amount) }}</td>
										<td class="text-start small">{{ log.bank_name || "—" }}</td>
										<td class="text-start small">{{ log.payment_id || "—" }}</td>
										<td>{{ log.payment_date ? $filters.formatDate(log.payment_date) : "—" }}</td>
										<td class="text-start small">{{ log.created_at ? $filters.formatDate(log.created_at) : "—" }}</td>
									</tr>
								</tbody>
							</table>
						</div>
					</div>
				</div>
			</div>
		</div>
	</div>
</template>

<script>
export default {
	name: "CourierDetails",
	data() {
		return {
			token: "",
			courierId: null,
			loading: true,
			loadError: "",

			courier: null,
			products: [],
			paymentLogs: [],
			summary: null,
			allProducts: [],
		};
	},
	computed: {
		primaryPayment() {
			const p = this.products.find((x) => x.has_payment && x.payment);
			return p?.payment || null;
		},
	},
	created() {
		this.courierId = this.$route.params.courier_id;
		if (!this.courierId) {
			this.loading = false;
			this.loadError = "Courier ID is missing";
			return;
		}
		this.init();
	},
	methods: {
		getToken() {
			if (localStorage.getItem("airotrack_staff")) {
				const staff = JSON.parse(localStorage.getItem("airotrack_staff"));
				if (staff.token) {
					this.token = staff.token;
					return;
				}
			}
			this.$swal("", "Session expired, please login again", "error");
			this.$router.push({ name: "login" });
		},

		async init() {
			this.getToken();
			try {
				await Promise.all([this.fetchAllProducts(), this.fetchCourierDetails()]);
			} catch (e) {
				this.loadError = e?.message || "Failed to load courier details";
			} finally {
				this.loading = false;
			}
		},

		fetchCourierDetails() {
			return this.$axios
				.get(`/staffs/courierDetails?courier_id=${encodeURIComponent(this.courierId)}`, {
					headers: { Authorization: "Bearer " + this.token },
				})
				.then((res) => {
					const data = res.data?.data || {};
					this.courier = data.courier || null;
					this.products = Array.isArray(data.products) ? data.products : [];
					this.paymentLogs = Array.isArray(data.payment_logs) ? data.payment_logs : [];
					this.summary = data.summary || null;
					if (!this.courier) {
						throw new Error("Courier not found");
					}
				})
				.catch((err) => {
					throw new Error(err.response?.data?.message || "Failed to load courier details");
				});
		},

		fetchAllProducts() {
			return this.$axios
				.get("/staffs/allProducts", { headers: { Authorization: "Bearer " + this.token } })
				.then((res) => {
					const data = res.data.data || res.data;
					this.allProducts = Array.isArray(data) ? data : data?.data || [];
				})
				.catch(() => {
					this.allProducts = [];
				});
		},

		productTypeLabel(t) {
			const product = this.allProducts.find((p) => String(p.id) === String(t));
			return product?.product_name || "";
		},

		userTypeLabel(t) {
			const map = { 1: "Dealer", 2: "Customer", 6: "Technician" };
			return map[String(t)] || "—";
		},

		courierStatusLabel(v) {
			const map = { 1: "Pending", 2: "Completed" };
			return map[Number(v)] || "—";
		},

		paymentStatusLabel(v) {
			const map = { 1: "Received", 2: "Not received", 3: "Partially received" };
			return map[Number(v)] || "—";
		},

		paymentStatusClass(v) {
			const n = Number(v);
			if (n === 1) return "bg-success";
			if (n === 2) return "bg-danger";
			if (n === 3) return "bg-warning";
			return "bg-secondary";
		},

		paymentModeLabel(v) {
			const map = { 1: "Cash", 2: "Online", 3: "Both" };
			return map[Number(v)] || "—";
		},

		formatMoney(value) {
			const n = parseFloat(value);
			if (isNaN(n)) return "0.00";
			return n.toFixed(2);
		},
	},
};
</script>
